/**
 * PDF Text Extractor
 * Uses pdfjs-dist to extract text content from gzip-compressed base64 PDF data
 * stored in localStorage (dms_documents.file_base64).
 */

import * as pdfjsLib from 'pdfjs-dist';
import { preprocessCanvasForOcr, type PreprocessOpts } from './ocrPreprocess';

// Configure worker — use local copy in /public
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

/**
 * Decompress gzip base64 → raw Uint8Array (PDF bytes)
 */
async function decompressGzipBase64(base64: string): Promise<Uint8Array> {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

  const DS = (window as any).DecompressionStream;
  if (typeof DS === 'undefined') {
    // Browser doesn't support DecompressionStream — assume raw PDF
    return bytes;
  }

  const ds = new DS('gzip');
  const writer = ds.writable.getWriter();
  writer.write(bytes);
  writer.close();

  const reader = ds.readable.getReader();
  const chunks: Uint8Array[] = [];

  while (true) {
    const { done, value }: { done: boolean; value: Uint8Array } = await reader.read();
    if (done) break;
    chunks.push(value);
  }

  const totalLen = chunks.reduce((s, c) => s + c.length, 0);
  const result = new Uint8Array(totalLen);
  let off = 0;
  for (const c of chunks) {
    result.set(c, off);
    off += c.length;
  }
  return result;
}

/**
 * Extract all text from a PDF stored as gzip-compressed base64.
 * Returns the full text content with pages separated by double-newlines.
 */
export async function extractTextFromPdf(base64: string): Promise<string> {
  try {
    const pdfBytes = await decompressGzipBase64(base64);

    const pdf = await pdfjsLib.getDocument({ data: pdfBytes.slice() }).promise;
    try {
      const pageTexts: string[] = [];

      // Extract text from all pages (limit to first 5 pages for performance)
      const maxPages = Math.min(pdf.numPages, 5);
      for (let i = 1; i <= maxPages; i++) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        const text = content.items
          .map((item: any) => item.str)
          .join(' ');
        pageTexts.push(text);
        page.cleanup();
      }

      return pageTexts.join('\n\n');
    } finally {
      await closePdf(pdf);
    }
  } catch (err) {
    console.warn('[pdfTextExtractor] Failed to extract text:', err);
    return '';
  }
}

// Tesseract.js loads its worker + core (wasm) + language data from a CDN by
// default. For go-live, drop self-hosted copies under /public and point these
// env vars at them (e.g. REACT_APP_TESSERACT_LANG_PATH=/tesseract) so the app
// doesn't depend on a third-party CDN and works offline / behind a firewall.
function tesseractOptions(): Record<string, string> {
  const o: Record<string, string> = {};
  if (process.env.REACT_APP_TESSERACT_WORKER_PATH) o.workerPath = process.env.REACT_APP_TESSERACT_WORKER_PATH;
  if (process.env.REACT_APP_TESSERACT_CORE_PATH) o.corePath = process.env.REACT_APP_TESSERACT_CORE_PATH;
  if (process.env.REACT_APP_TESSERACT_LANG_PATH) o.langPath = process.env.REACT_APP_TESSERACT_LANG_PATH;
  return o;
}

// =============================================
// Releasing what a read holds on to
// =============================================
// A bulk upload reads hundreds of pages in one sitting, and every one of these was
// once left for the garbage collector: the pdf.js document (its worker keeps the
// decoded page image — tens of MB for a 300-dpi scan), the render canvas, and a whole
// OCR engine per page. The tab ran out of memory a couple of hundred pages in and
// died mid-file. Everything opened here is now closed here.

/** Close a pdf.js document so its worker frees the parsed file and decoded images. */
async function closePdf(pdf: any): Promise<void> {
  try { await pdf?.destroy(); } catch { /* already closed */ }
}

/** Hand a canvas's pixel buffer back now instead of whenever it is collected. */
function releaseCanvas(canvas: HTMLCanvasElement | null | undefined): void {
  if (!canvas) return;
  canvas.width = 0;
  canvas.height = 0;
}

// ONE OCR engine for the whole app, used one page at a time. It is dropped after a
// minute of no use, and swapped for a fresh one every so often because the engine's
// own (wasm) memory only ever grows.
const OCR_WORKER_MAX_USES = 40;
const OCR_WORKER_IDLE_MS = 60 * 1000;
let ocrWorker: any = null;
let ocrWorkerUses = 0;
let ocrIdleTimer: ReturnType<typeof setTimeout> | null = null;
let ocrChain: Promise<unknown> = Promise.resolve();

async function dropOcrWorker(): Promise<void> {
  const worker = ocrWorker;
  ocrWorker = null;
  ocrWorkerUses = 0;
  try { await worker?.terminate(); } catch { /* already gone */ }
}

/** OCR one rendered page with the shared engine. Throws if the engine can't run. */
function recognizeCanvas(canvas: HTMLCanvasElement): Promise<string> {
  const run = async (): Promise<string> => {
    if (ocrIdleTimer) { clearTimeout(ocrIdleTimer); ocrIdleTimer = null; }
    try {
      if (!ocrWorker) {
        const Tesseract = (await import('tesseract.js')).default;
        ocrWorker = await Tesseract.createWorker('eng', 1, tesseractOptions());
      }
      const { data } = await ocrWorker.recognize(canvas);
      if (++ocrWorkerUses >= OCR_WORKER_MAX_USES) await dropOcrWorker();
      return data?.text || '';
    } catch (err) {
      await dropOcrWorker();      // never reuse an engine that just failed
      throw err;
    } finally {
      ocrIdleTimer = setTimeout(() => {
        ocrIdleTimer = null;
        ocrChain = ocrChain.then(dropOcrWorker, dropOcrWorker);
      }, OCR_WORKER_IDLE_MS);
    }
  };
  const result = ocrChain.then(run, run);
  ocrChain = result.catch(() => { /* the caller gets the error */ });
  return result;
}

// Render one page for OCR. Accuracy is dominated by render resolution, and a fixed
// scale starves a small media box (a thermal-receipt PDF ~157pt wide → only ~315px
// at scale 2), so scale toward ~1600px wide — never below 2, capped at 10 to bound
// canvas memory on large pages.
const OCR_TARGET_W = 1600;
async function renderPageForOcr(page: any, preprocess: boolean | PreprocessOpts): Promise<HTMLCanvasElement | null> {
  const base = page.getViewport({ scale: 1 });
  const viewport = page.getViewport({ scale: Math.max(2, Math.min(10, OCR_TARGET_W / base.width)) });
  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  // intent 'print': no requestAnimationFrame, which browsers pause in a hidden tab.
  await page.render({ canvasContext: ctx, viewport, intent: 'print' } as any).promise;
  if (preprocess !== false) {
    preprocessCanvasForOcr(canvas, typeof preprocess === 'object' ? preprocess : {});
  }
  return canvas;
}

/** One page of a PDF as read for document-boundary detection (see pageGrouping.ts). */
export interface PdfPageText {
  text: string;
  width: number;
  height: number;
  /** true = no usable text layer, the text came from OCR */
  ocr: boolean;
}

// Below this many non-space chars a page has no usable text layer (a scan/photo).
const PAGE_TEXT_MIN_CHARS = 40;

/**
 * Read EVERY page of a PDF on its own: the text layer where there is one, OCR
 * (Tesseract, the shared engine) for scanned pages. Lines are kept apart so the
 * caller can tell the top of a page from the bottom. Throws if the PDF can't be
 * opened; a page that fails to read comes back with empty text.
 */
export async function readPdfPageTexts(
  pdfBytes: Uint8Array, onPage?: (done: number, total: number) => void,
): Promise<PdfPageText[]> {
  const pdf = await pdfjsLib.getDocument({ data: pdfBytes.slice() }).promise;
  const pages: PdfPageText[] = [];
  try {
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const { width, height } = page.getViewport({ scale: 1 });
      let text = '';
      let ocr = false;
      try {
        const content = await page.getTextContent();
        text = content.items.map((item: any) => (item.str || '') + (item.hasEOL ? '\n' : ' ')).join('');
        if (text.replace(/\s/g, '').length < PAGE_TEXT_MIN_CHARS) {
          const canvas = await renderPageForOcr(page, true);
          if (canvas) {
            try {
              text = (await recognizeCanvas(canvas)) || text;
              ocr = true;
            } finally {
              releaseCanvas(canvas);
            }
          }
        }
      } catch (err) {
        console.warn(`[pdfTextExtractor] Page ${i} could not be read:`, err);
      }
      page.cleanup();
      pages.push({ text, width, height, ocr });
      onPage?.(i, pdf.numPages);
    }
  } finally {
    await closePdf(pdf);
  }
  return pages;
}

/** Pictures of a PDF's first page for the AI reader (JPEG, base64, no data: prefix). */
export interface FirstPageImages {
  /** the whole page, scaled to fit the reader's working size */
  page: string;
  /** the top part of the page at full sharpness — where a date is usually written. '' on a short page */
  top: string;
}

// The AI reader works on images up to about this many pixels on the long side; anything
// larger is shrunk before it looks. A whole letter page at that size leaves a pen-written
// date only a few pixels tall (a 0 was read as a 2), so the top of the page is also sent
// on its own, where the same limit gives it more than twice the detail.
const AI_IMAGE_EDGE = 1568;
const TOP_FRACTION = 0.36;

function canvasJpeg(source: HTMLCanvasElement, sx: number, sy: number, sw: number, sh: number, scale: number): string {
  const out = document.createElement('canvas');
  out.width = Math.max(1, Math.round(sw * scale));
  out.height = Math.max(1, Math.round(sh * scale));
  try {
    const ctx = out.getContext('2d');
    if (!ctx) return '';
    ctx.fillStyle = '#fff';                       // JPEG has no transparency
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(source, sx, sy, sw, sh, 0, 0, out.width, out.height);
    return out.toDataURL('image/jpeg', 0.85).split(',')[1] || '';
  } finally {
    releaseCanvas(out);
  }
}

/**
 * The first page of a PDF as pictures for the AI reader: the whole page, plus a sharp
 * close-up of its top. Null when the page can't be drawn.
 */
export async function renderPdfFirstPageImages(base64: string): Promise<FirstPageImages | null> {
  let pdf: any = null;
  let canvas: HTMLCanvasElement | null = null;
  try {
    const pdfBytes = await decompressGzipBase64(base64);
    pdf = await pdfjsLib.getDocument({ data: pdfBytes.slice() }).promise;
    const page = await pdf.getPage(1);
    const base = page.getViewport({ scale: 1 });
    // Draw once, wide enough for the close-up (never more than 4x the page's own size).
    const viewport = page.getViewport({ scale: Math.min(4, (AI_IMAGE_EDGE * 1.6) / base.width) });
    canvas = document.createElement('canvas');
    canvas.width = Math.round(viewport.width);
    canvas.height = Math.round(viewport.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport, intent: 'print' } as any).promise;
    page.cleanup();

    const w = canvas.width;
    const h = canvas.height;
    const whole = canvasJpeg(canvas, 0, 0, w, h, Math.min(1, AI_IMAGE_EDGE / Math.max(w, h)));
    if (!whole) return null;
    // A short receipt already fits at full sharpness — no close-up needed.
    const topH = Math.round(h * TOP_FRACTION);
    const top = Math.max(w, h) > AI_IMAGE_EDGE
      ? canvasJpeg(canvas, 0, 0, w, topH, Math.min(1, AI_IMAGE_EDGE / Math.max(w, topH)))
      : '';
    return { page: whole, top };
  } catch (err) {
    console.warn('[pdfTextExtractor] Could not draw the first page:', err);
    return null;
  } finally {
    releaseCanvas(canvas);
    await closePdf(pdf);
  }
}

export interface OcrPagesOpts {
  /** how many pages to OCR (default 3) */
  maxPages?: number;
  /** OCR only the top fraction of each page — 0.3 = top 30% (default 1 = whole page) */
  topFraction?: number;
  /**
   * Clean each rendered page before OCR (grayscale → normalize → Otsu binarize)
   * to cut noise on scans/photos. `true` = defaults, a PreprocessOpts to tune, or
   * `false` to skip (e.g. a clean digital render). Default `true`.
   */
  preprocess?: boolean | PreprocessOpts;
}

/**
 * Render PDF pages to a canvas and OCR them with Tesseract.js — FREE, fully
 * client-side, no API cost. Used as a fallback when the PDF has no text layer
 * (a scan/photo), or to read a logo/letterhead the text layer can't see (pass a
 * small topFraction to OCR just the header). tesseract.js is lazy-imported so it
 * only downloads when OCR is actually needed. Best-effort: returns '' on failure.
 */
export async function ocrPdfPages(base64: string, opts: OcrPagesOpts = {}): Promise<string> {
  const { maxPages = 3, topFraction = 1, preprocess = true } = opts;
  let pdf: any = null;
  try {
    const pdfBytes = await decompressGzipBase64(base64);
    pdf = await pdfjsLib.getDocument({ data: pdfBytes.slice() }).promise;

    const n = Math.min(pdf.numPages, maxPages);
    const parts: string[] = [];
    // OCR accuracy is dominated by render resolution. A fixed scale starves a
    // small media box (e.g. a thermal-receipt PDF ~157pt wide → only ~315px at
    // scale 2). Scale each page toward ~1600px wide instead — never below 2,
    // capped at 10 to bound canvas memory on large pages.
    const TARGET_W = 1600;
    for (let i = 1; i <= n; i++) {
      const page = await pdf.getPage(i);
      const base = page.getViewport({ scale: 1 });
      const scale = Math.max(2, Math.min(10, TARGET_W / base.width));
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      canvas.width = viewport.width;
      // A shorter canvas clips the page to its top — cheap way to OCR just the header.
      canvas.height = topFraction < 1
        ? Math.max(1, Math.round(viewport.height * topFraction))
        : viewport.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) continue;
      try {
        // intent 'print' = pdf.js renders without requestAnimationFrame, which the
        // browser pauses in a hidden/covered tab (the upload would sit on this page
        // until the user came back).
        await page.render({ canvasContext: ctx, viewport, intent: 'print' } as any).promise;
        if (preprocess !== false) {
          preprocessCanvasForOcr(canvas, typeof preprocess === 'object' ? preprocess : {});
        }
        parts.push(await recognizeCanvas(canvas));
      } finally {
        releaseCanvas(canvas);
        page.cleanup();
      }
    }
    return parts.join('\n').trim();
  } catch (err) {
    console.warn('[pdfTextExtractor] OCR failed:', err);
    return '';
  } finally {
    await closePdf(pdf);
  }
}
